import React, { useEffect, useRef, useState } from "react";
import { Person } from "../../../../model/Person";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, GridSize, Typography, useTheme } from "@material-ui/core";
import { FormikProps } from "formik";
import { ApiBackend } from "../../../../providers/apibackend";
import { PersonResult } from "../../../../model/PersonResult";
import { vasaloppetDateTimeGetterFormatter } from "../../../../utilities/date";
import { orderBy } from "../../../../utilities/sortmodel";
import { useReactToPrint } from "react-to-print";
import PrintableComponent from "./PrintableComponent/PrintableComponent";

interface IProps extends FormikProps<Person> {
    onClose: () => void;
}

type TypedGridItemProps = {
    label: string;
    propName: keyof Person;
    values: Person
};
const TypedGridItem = ({ label, propName, values }: TypedGridItemProps) => {
    return (
        <GenericGridItem label={label} value={values[propName]} gs1={4} gs2={8} boldLabel={true} />
    );
};

type GridItemProps = {
    label: string;
    value?: any;
    formatter?: () => string | number;
};
const GridItem = ({ label, value, formatter }: GridItemProps) => {
    let safeValue = value ?? "";

    if (formatter) {
        safeValue = formatter();
    }

    return (
        <GenericGridItem label={label} value={safeValue} gs1={4} gs2={8} boldLabel={true} />
    );
};

type GenericGridItemProps = {
    label: string;
    value?: any;
    gs1: GridSize;
    gs2: GridSize;
    boldLabel: boolean;
}

const GenericGridItem = ({ label, value, gs1, gs2, boldLabel }: GenericGridItemProps) => {
    const style = boldLabel ? { fontWeight: "bold" } : null;

    return (
        <Grid container>
            <Grid item xs={gs1}>
                <Typography variant="caption" style={style}>
                    {label}
                </Typography>
            </Grid>
            <Grid item xs={gs2}>
                <Typography variant="caption">
                    {value}
                </Typography>
            </Grid>
        </Grid>
    );
};

const getRaceName = (res: PersonResult) => {
    let raceName = res.raceName;

    if (res.raceType === "relay") {
        raceName += !!res.teamOrder ? ` - Sträcka ${res.teamOrder}` : " - Lagledare"
    }

    return raceName;
};

const PersonPrintDialog = ({ values, onClose }: IProps) => {
    const [results, setResults] = useState<PersonResult[]>([]);
    const [isPrinting, setIsPrinting] = useState(false);

    const contentRef = useRef<HTMLDivElement>(null);

    const handlePrint = useReactToPrint({
        contentRef: contentRef,
        onBeforePrint: () => {
            return new Promise((resolve) => {
                setIsPrinting(true);
                resolve();
            });
        },
        onAfterPrint: () => {
            setIsPrinting(false);
        },
    });

    const printDate = vasaloppetDateTimeGetterFormatter(new Date());
    const theme = useTheme();
    const api = new ApiBackend();

    const headerStyle: React.CSSProperties = {
        background: theme.palette.primary.dark,
        color: theme.palette.primary.main,
        padding: 5,
        marginBottom: 2,
        marginTop: 10
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        const res = await api.getPersonResults(values.id)
        setResults(res);
    };

    const Header = () => {
        return (
            <Grid container spacing={10}>
                <Grid item xs={4}>
                    <div className="asset-logotype"></div>
                </Grid>
                <Grid item xs={8}>
                    <TypedGridItem label="Vasa ID" propName="vasaId" values={values} />
                    <TypedGridItem label="Förnamn" propName="firstName" values={values} />
                    <TypedGridItem label="Efternamn" propName="lastName" values={values} />
                    <TypedGridItem label="Födelsedatum" propName="birthDay" values={values} />
                    <TypedGridItem label="Nationalitet" propName="nationality" values={values} />
                    <GridItem label="Utskrift" formatter={() => printDate} />
                </Grid>
            </Grid>
        );
    };

    const PrintHeader = () => {
        return (
            <div style={{ padding: 20 }}>
                <Header />
            </div>
        );
    };

    const PrintFooter = () => {
        return (
            <div style={{ padding: 20 }}><Typography variant="caption">{printDate}</Typography></div>
        );
    };

    const PrintContent = () => {
        return (<>
            <Top forPrint />
            <Results />
        </>);
    };

    const Top = ({ forPrint }: { forPrint?: boolean; }) => {
        const hasMedalYears = values.counters?.some(x => x.counter_key == "MJ");
        const winterCounters = values.counters?.filter(x => x.category === "Vinter" && x.showOnPrint);
        const summerCounters = values.counters?.filter(x => x.category === "Sommar" && x.showOnPrint);

        const subHeaderStyle: React.CSSProperties = {
            background: "#999",
            color: theme.palette.primary.main,
            padding: 5,
            marginBottom: 2
        };

        const wrapperStyle: React.CSSProperties = forPrint ? { paddingTop: 20 } : null;

        return (
            <Grid container spacing={2} >
                <Grid item xs={6} style={wrapperStyle}>
                    <Box>
                        <Box style={headerStyle}>
                            Adressuppgifter
                        </Box>
                        <Box style={{ padding: 5 }}>
                            <TypedGridItem label="Adress" propName="address" values={values} />
                            {values.address_line2 &&
                                <TypedGridItem label="Adress2" propName="address_line2" values={values} />
                            }
                            <TypedGridItem label="Postnummer" propName="postCode" values={values} />
                            <TypedGridItem label="Postort" propName="city" values={values} />
                            <TypedGridItem label="Län" propName="county" values={values} />
                            <TypedGridItem label="Land" propName="country" values={values} />
                        </Box>
                    </Box>
                    <Box>
                        <Box style={headerStyle}>
                            Övrigt
                        </Box>
                        <Box style={{ padding: 5 }}>
                            <TypedGridItem label="E-post" propName="email" values={values} />
                            <TypedGridItem label="Mobilnr." propName="mobileNumber" values={values} />
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={6} style={wrapperStyle}>
                    <Box style={headerStyle}>
                        Antal lopp
                    </Box>
                    <Box style={{ padding: 5 }}>
                        <GridItem label="Medaljår"
                            formatter={() => {
                                return hasMedalYears ? values.counters?.find(x => x.counter_key == "MJ")?.count : 0;
                            }}
                        />
                    </Box>
                    <Box>
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Box>
                                    <Box style={subHeaderStyle}>
                                        Vinterlopp
                                    </Box>
                                    <Box style={{ padding: 1 }}>
                                        {winterCounters && orderBy(winterCounters, "name").map((x, key) => {
                                            return <GenericGridItem key={key} label={x.name} value={x.count} gs1={10} gs2={2} boldLabel={false} />
                                        })}
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box>
                                    <Box style={subHeaderStyle}>
                                        Sommarlopp
                                    </Box>
                                    <Box style={{ padding: 1 }}>
                                        {summerCounters && orderBy(summerCounters, "name").map((x, key) => {
                                            return <GenericGridItem key={key} label={x.name} value={x.count} gs1={10} gs2={2} boldLabel={false} />
                                        })}
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        );
    };

    const Results = () => {
        const alignCenter: React.CSSProperties = { textAlign: "center" };

        let rows: React.JSX.Element[] = [];

        for (let i = 0; i < results.length; i += 2) {
            const res = results[i];

            const columns = [
                <td key="a_year"><Typography variant="caption">{res.year}</Typography></td>,
                <td key="a_raceName"><Typography variant="caption">{getRaceName(res)}</Typography></td>,
                <td key="a_raceTimer"><Typography variant="caption">{res.raceTime}</Typography></td>,
                <td key="a_placeGender" style={alignCenter}><Typography variant="caption">{res.placeGender}</Typography></td>,
                <td key="a_spacer" style={{ borderLeft: "1px solid #ccc" }}></td>
            ];

            if (results[i + 1]) {
                const secRes = results[i + 1];
                columns.push(...[
                    <td key="b_year"><Typography variant="caption">{secRes.year}</Typography></td>,
                    <td key="b_raceName"><Typography variant="caption">{getRaceName(secRes)}</Typography></td>,
                    <td key="b_raceTime"><Typography variant="caption">{secRes.raceTime}</Typography></td>,
                    <td key="b_placeGender" style={alignCenter}><Typography variant="caption">{secRes.placeGender}</Typography></td>
                ]);
            }

            rows.push(<tr key={i}>{columns}</tr>);
        }

        return (
            <table cellSpacing="0" cellPadding="5" style={{ width: "100%", marginTop: 20, tableLayout: "fixed" }}>
                <colgroup>
                    <col width={50} />
                    <col width={250} />
                    <col width={60} />
                    <col width={60} />
                    <col width={10} />
                    <col width={50} />
                    <col width={250} />
                    <col width={60} />
                    <col width={60} />
                </colgroup>
                <thead style={headerStyle}>
                    <tr>
                        <td colSpan={9}>
                            Historik
                        </td>
                    </tr>
                    <tr>
                        <td></td>
                        <td><Typography variant="caption">Tävling</Typography></td>
                        <td><Typography variant="caption">Tid</Typography></td>
                        <td style={alignCenter}><Typography variant="caption">Placering</Typography></td>
                        <td style={{ minWidth: 10 }}>&nbsp;</td>
                        <td></td>
                        <td><Typography variant="caption">Tävling</Typography></td>
                        <td><Typography variant="caption">Tid</Typography></td>
                        <td style={alignCenter}><Typography variant="caption">Placering</Typography></td>
                    </tr>
                </thead>
                <tbody>
                    {rows}
                </tbody>
            </table>
        )
    };

    const render = () => {
        return (
            <Dialog
                disableEscapeKeyDown
                fullWidth
                maxWidth="md"
                aria-labelledby="person-print-dialog-title"
                open={true}>
                <DialogTitle id="person-print-dialog-title">
                    Skriv ut person
                </DialogTitle>
                <DialogContent dividers style={{ padding: 0, overflowX: "hidden" }}>
                    {!isPrinting &&
                        <div style={{ padding: 16 }}>
                            <Header />
                            <Top />
                            <Results />
                        </div>
                    }
                    {isPrinting &&
                        <PrintableComponent
                            Header={PrintHeader}
                            Footer={PrintFooter}
                            Content={PrintContent}
                            reference={contentRef}
                            style={{ margin: 16 }}
                        />
                    }
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="secondary"
                        disabled={isPrinting}
                        onClick={handlePrint}>
                        Skriv ut
                    </Button>
                    <Button variant="contained" color="secondary" style={{ marginLeft: 5 }}
                        disabled={isPrinting}
                        onClick={onClose}>
                        Stäng
                    </Button>
                </DialogActions>
            </Dialog>
        )
    };

    return render();
};

export default PersonPrintDialog;
